import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import AuthGuard from "../components/AuthGuard";
import Landing from "../pages/presentation/Landing";
import PublicMap from "../pages/publicMap";
import { ROUTES } from "../constants";
import AuthVisibilityFilter from "../components/AuthVisibilityFilter";
import {
  People,
  Map,
  Group,
  Waves,
  WbSunny,
  Straighten,
  Description,
  LocationOn,
  Contacts,
  HourglassBottom,
  AddCircleOutline,
  AssignmentTurnedIn,
  Plumbing,
  ElectricalServices,
  Science,
  Speed,
} from "@mui/icons-material";
import AdminVisibilityFilter from "../components/AdminVisibilityFilter";
import AdminGuard from "../components/AdminGuard";
import UsersManagement from "../pages/usersManagement/UsersManagement";
import MetersManagement from "../pages/metersManagement/MetersManagement";
import PumpageReport from "../pages/pumpageReport/PumpageReport";
import DroughtStageUpdate from "../pages/droughtStageUpdate/DroughtStageUpdate";
import DroughtProductionCutback from "../pages/droughtProductionCutback/DroughtProductionCutback";
import WaterLevelRecordsForm from "../pages/waterLevelRecords/WaterLevelRecords";
import WaterQuality from "../pages/waterQuality/WaterQuality";
import ContactsPage from "../pages/contactsManagement/ContactsPage";
import WaterServiceConnectionsPage from "../pages/waterServiceConnections/WaterServiceConnectionsPage";
import PermitsPage from "../pages/permitsManagement/PermitsPage";
import AllocationProfilesPage from "../pages/permitsManagement/AllocationProfilesPage";
import WellRegistrationsPage from "../pages/wellRegistrations/WellRegistrationsPage";
import WellsPage from "../pages/wellsManagement/WellsPage";
import NewWellRegistrationPage from "../pages/newWellRegistration/NewWellRegistrationPage";
import PublicForms from "../pages/forms/PublicForms";
import PendingWellRegistrationPage from "../pages/pendingWellRegistration/PendingWellRegistrationPage";
import ExistingWellRegistrationPage from "../pages/existingWellRegistration/ExistingWellRegistrationPage";
import NewWaterServiceConnectionPage from "../pages/newWaterServiceConnection/NewWaterServiceConnectionPage";
import PendingWaterServiceConnectionPage from "../pages/pendingWaterServiceConnection/PendingWaterServiceConnectionPage";
import PwsContactsPage from "../pages/pwsContactsManagement/PwsContactsPage";

const landingRoutes = {
  id: "Landing Page",
  path: ROUTES.LANDING,
  component: Landing,
  children: null,
};

const publicMapRoute = {
  header: "Tools",
  id: "Interactive Map",
  icon: <Map />,
  path: ROUTES.MAP,
  component: PublicMap,
  guard: AuthGuard,
  visibilityFilter: AuthVisibilityFilter,
  config: {
    drawerOpenOverride: false,
  },
};

const publicFormsRoute = {
  id: "Public Forms",
  path: ROUTES.PUBLIC_FORMS,
  component: PublicForms,
  config: {},
};

const newWellRegistrationRoute = {
  header: "Forms",
  id: "Well Construction Notification Application",
  icon: <AddCircleOutline />,
  path: ROUTES.NEW_WELL_REGISTRATION,
  component: NewWellRegistrationPage,
};

const existingWellRegistrationRoute = {
  id: "Existing Well Registration Request",
  icon: <AssignmentTurnedIn />,
  path: ROUTES.EXISTING_WELL_REGISTRATION,
  component: ExistingWellRegistrationPage,
};

const newWaterServiceConnectionRoute = {
  id: "New Water Service Connection",
  icon: <Plumbing />,
  path: ROUTES.NEW_WATER_SERVICE_CONNECTION,
  component: NewWaterServiceConnectionPage,
};

const DynamicFormRoutes = {
  children: [
    {
      path: ROUTES.PENDING_WELL_REGISTRATION,
      component: PendingWellRegistrationPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
    },
    {
      path: ROUTES.PENDING_WATER_SERVICE_CONNECTION,
      component: PendingWaterServiceConnectionPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
    },
  ],
};

const pumpageReportRoute = {
  header: "Reporting",
  id: "Pumpage Report",
  icon: <Waves />,
  path: ROUTES.PUMPAGE_REPORT,
  component: PumpageReport,
  guard: AuthGuard,
  visibilityFilter: AuthVisibilityFilter,
  config: {},
};

const waterLevelRecordsRoute = {
  id: "Water Level Records",
  icon: <Straighten />,
  path: ROUTES.WATER_LEVEL_REPORT,
  component: WaterLevelRecordsForm,
  guard: AuthGuard,
  visibilityFilter: AuthVisibilityFilter,
  config: {},
};

const waterQualityRoute = {
  id: "Water Quality",
  icon: <Science />,
  path: ROUTES.WATER_QUALITY,
  component: WaterQuality,
  guard: AdminGuard,
  visibilityFilter: AdminVisibilityFilter,
  config: {},
};

const usersManagementRoute = {
  header: "Records Management",
  id: "Users Management",
  icon: <Group />,
  path: ROUTES.USERS_MANAGEMENT,
  component: UsersManagement,
  guard: AdminGuard,
  visibilityFilter: AdminVisibilityFilter,
  config: {},
};

const metersManagementRoute = {
  id: "Meters",
  icon: <Speed />,
  path: ROUTES.METERS_MANAGEMENT,
  component: MetersManagement,
  guard: AdminGuard,
  visibilityFilter: AdminVisibilityFilter,
  config: {},
};

const contactsManagementRoutes = {
  id: "Contacts",
  icon: <Contacts />,
  children: [
    {
      name: "Rolodex",
      path: ROUTES.CONTACTS_MANAGEMENT,
      component: ContactsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
    {
      name: "Public Water System Operators",
      path: ROUTES.PUBLIC_WATER_SYSTEM_CONTACTS_MANAGEMENT,
      component: PwsContactsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
  ],
};

const wellsManagementRoutes = {
  id: "Wells",
  icon: <LocationOn />,
  children: [
    {
      name: "Wells",
      path: ROUTES.WELLS_MANAGEMENT,
      component: WellsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
    {
      name: "Archived Well Registrations",
      path: ROUTES.WELL_REGISTRATIONS_MANAGEMENT,
      component: WellRegistrationsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
  ],
};

const permitsManagementRoutes = {
  id: "Permits",
  icon: <Description />,
  children: [
    {
      name: "Permits",
      path: ROUTES.PERMITS_MANAGEMENT,
      component: PermitsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
    {
      name: "Allocation Profiles",
      path: ROUTES.ALLOCATION_PROFILES_MANAGEMENT,
      component: AllocationProfilesPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
    {
      name: "Water Service Connections",
      path: ROUTES.WATER_SERVICE_CONNECTIONS_MANAGEMENT,
      component: WaterServiceConnectionsPage,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
  ],
};

const droughtManagementRoutes = {
  id: "Drought",
  icon: <WbSunny />,
  children: [
    {
      name: "Drought Stage",
      path: ROUTES.DROUGHT_STAGE_UPDATE,
      component: DroughtStageUpdate,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
    {
      name: "Production Cutback & Settlement",
      path: ROUTES.DROUGHT_PRODUCTION_CUTBACK,
      component: DroughtProductionCutback,
      guard: AdminGuard,
      visibilityFilter: AdminVisibilityFilter,
      config: {},
    },
  ],
};

const pendingWellRegistrationsManagementRoute = {
  header: "Pending Approvals",
  id: "Well Registrations",
  icon: <HourglassBottom />,
  path: ROUTES.PENDING_WELL_REGISTRATIONS_MANAGEMENT,
  component: PendingWellRegistrationPage,
  guard: AdminGuard,
  visibilityFilter: AdminVisibilityFilter,
};

const pendingWaterServiceConnectionsManagementRoute = {
  id: "Water Service Connection Requests",
  icon: <ElectricalServices />,
  path: ROUTES.PENDING_WATER_SERVICE_CONNECTIONS_MANAGEMENT,
  component: PendingWaterServiceConnectionPage,
  guard: AdminGuard,
  visibilityFilter: AdminVisibilityFilter,
};

const accountRoutes = {
  id: "Account",
  icon: <People />,
  children: [
    {
      path: ROUTES.USER_LOGOUT,
      name: "Logout",
      component: function Logout() {
        const { logout } = useAuth0();
        void logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      },
    },
  ],
  guard: AuthGuard,
};

export const dashboardLayoutRoutes = [
  publicFormsRoute,
  newWellRegistrationRoute,
  existingWellRegistrationRoute,
  newWaterServiceConnectionRoute,
  DynamicFormRoutes,
  pumpageReportRoute,
  waterLevelRecordsRoute,
  waterQualityRoute,
  usersManagementRoute,
  metersManagementRoute,
  contactsManagementRoutes,
  wellsManagementRoutes,
  permitsManagementRoutes,
  droughtManagementRoutes,
  accountRoutes,
];

export const dashboardMaxContentLayoutRoutes = [publicMapRoute];

export const authLayoutRoutes = [];

export const presentationLayoutRoutes = [landingRoutes];

export const sidebarRoutes = [
  publicMapRoute,
  newWellRegistrationRoute,
  existingWellRegistrationRoute,
  newWaterServiceConnectionRoute,
  pumpageReportRoute,
  waterLevelRecordsRoute,
  waterQualityRoute,
  usersManagementRoute,
  contactsManagementRoutes,
  permitsManagementRoutes,
  wellsManagementRoutes,
  metersManagementRoute,
  droughtManagementRoutes,
  pendingWellRegistrationsManagementRoute,
  pendingWaterServiceConnectionsManagementRoute,
];
